import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db';
import { functionError } from '@/lib/functionError';
import { toast } from '@/stores/toastStore';
import { useRows } from '@/hooks/useLive';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import { CheckboxInput, TextInput } from '@/components/ui/Field';
import type { Setting } from '@/types/models';

interface GroupRecipient {
  id: string;
  email: string;
  full_name: string | null;
  role_note: string | null;
  daily: boolean;
  monthly: boolean;
  is_active: boolean;
}

/**
 * The group's own reports (0040): production by unit, the fleet and the
 * workshop's cost by site, daily and monthly, to the group's own list.
 * Kept by quarry managers and administrators; it carries costs, so only
 * management belongs on it.
 */
export function GroupReports({ canEdit, onSent }: { canEdit: boolean; onSent: () => void }) {
  const settings = useRows<Setting>('settings');
  const daily = settings?.find((s) => s.key === 'group_daily_enabled');
  const monthly = settings?.find((s) => s.key === 'group_monthly_enabled');
  const [list, setList] = useState<GroupRecipient[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState<'group' | 'group_monthly' | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('group_recipients').select('*').is('deleted_at', null).order('email');
    if (error) { toast.error(`Could not read the group's list: ${error.message}`); return; }
    setList((data ?? []) as GroupRecipient[]);
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function toggle(key: 'group_daily_enabled' | 'group_monthly_enabled', on: boolean) {
    const { data, error } = await supabase.from('settings').update({ value: on }).eq('key', key).select().single();
    if (error) { toast.error(error.message); return; }
    await db.settings.put(data as Setting);
    toast.success(`${key === 'group_daily_enabled' ? 'Group daily' : 'Group monthly'} report ${on ? 'switched on' : 'paused'}`);
  }

  async function add() {
    if (!email.trim()) return;
    setBusy(true);
    const { error } = await supabase.from('group_recipients').insert({
      email: email.trim().toLowerCase(), full_name: name.trim() || null, role_note: role.trim() || null,
      daily: true, monthly: true, is_active: true,
    });
    setBusy(false);
    if (error) {
      toast.error(error.code === '23505' ? 'That address is already on the list.'
        : error.code === '23514' ? 'That does not look like an email address.' : error.message);
      return;
    }
    setEmail(''); setName(''); setRole('');
    toast.success('Recipient added');
    await load();
  }

  async function update(id: string, patch: Partial<GroupRecipient> & { deleted_at?: string }) {
    const { error } = await supabase.from('group_recipients').update(patch).eq('id', id);
    if (error) { toast.error(error.message); return; }
    if (patch.deleted_at) toast.success('Recipient removed');
    await load();
  }

  async function test(report: 'group' | 'group_monthly') {
    if (!testTo.trim()) return;
    setTesting(report);
    try {
      const { data, error } = await supabase.functions.invoke('daily-report-email', {
        body: { report, test_to: testTo.trim(), triggered_by: 'manual' },
      });
      if (error) {
        const said = await functionError(error);
        if (said) { toast.error(said); return; }
        throw error;
      }
      const res = data as { status?: string; error?: string };
      if (res?.status === 'sent') toast.success(`Test sent to ${testTo.trim()}`);
      else toast.error(res?.error ?? `Not sent: ${res?.status ?? 'unknown result'}`);
    } catch (err) {
      toast.error(`Could not reach the mail function. Deploy it with "npx supabase functions deploy daily-report-email". (${
        err instanceof Error ? err.message : String(err)})`);
    } finally {
      setTesting(null);
      onSent();
    }
  }

  return (
    <Panel title={`Group reports · ${list?.length ?? 0}`}
      actions={<>
        <StatusChip tone={daily?.value ? 'green' : 'grey'}>Daily {daily?.value ? 'on' : 'off'}</StatusChip>
        <StatusChip tone={monthly?.value ? 'green' : 'grey'}>Monthly {monthly?.value ? 'on' : 'off'}</StatusChip>
      </>}>
      <p className="small muted" style={{ marginTop: 0 }}>
        The group's own email, to its own list: each morning, production by unit (the quarry and every branch against
        its target), the fleet by site, what is down and the month's workshop cost; and on the monthly send day, the
        month that has ended, with the costliest vehicles. It carries costs, so keep it to management. It goes out at
        the same hour as the quarry's report, through the same mail server.
      </p>
      <div className="row" style={{ gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <CheckboxInput id="g-daily" label="Send the group daily report automatically"
          value={Boolean(daily?.value)} disabled={!canEdit || !daily}
          onChange={(v) => void toggle('group_daily_enabled', v)} />
        <CheckboxInput id="g-monthly" label="Send the group monthly report automatically"
          value={Boolean(monthly?.value)} disabled={!canEdit || !monthly}
          onChange={(v) => void toggle('group_monthly_enabled', v)} />
      </div>
      {!daily && (
        <p className="small muted">The group reports are not in this database yet. Apply migration <code>0040</code>.</p>
      )}

      {list && list.length === 0 && <p className="small muted">Nobody is on the group's list yet.</p>}
      {list && list.length > 0 && (
        <div className="table-wrap">
          <table className="table group-recipients">
            <thead>
              <tr>
                <th>Email</th><th>Name</th><th>Role</th>
                <th style={{ textAlign: 'center' }}>Daily</th>
                <th style={{ textAlign: 'center' }}>Monthly</th>
                <th style={{ textAlign: 'center' }}>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.email}</strong></td>
                  <td>{r.full_name ?? '—'}</td>
                  <td>{r.role_note ?? '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.daily} disabled={!canEdit} aria-label={`Group daily to ${r.email}`}
                      onChange={(e) => void update(r.id, { daily: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.monthly} disabled={!canEdit} aria-label={`Group monthly to ${r.email}`}
                      onChange={(e) => void update(r.id, { monthly: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.is_active} disabled={!canEdit} aria-label={`${r.email} active on the group list`}
                      onChange={(e) => void update(r.id, { is_active: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canEdit && (
                      <button className="btn btn--ghost" style={{ minHeight: 28 }}
                        onClick={() => void update(r.id, { deleted_at: new Date().toISOString() })}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && (
        <>
          <div className="form-grid" style={{ marginTop: 'var(--sp-3)' }}>
            <TextInput id="g-new-email" label="Email" type="email" value={email} onChange={setEmail} />
            <TextInput id="g-new-name" label="Name" value={name} onChange={setName} />
            <TextInput id="g-new-role" label="Role" value={role} onChange={setRole} help="Managing director, financial manager…" />
            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 22 }}>
              <Button variant="secondary" loading={busy} disabled={!email.trim()} onClick={() => void add()}>
                Add to the group list
              </Button>
            </div>
          </div>
          <div className="form-grid" style={{ marginTop: 'var(--sp-2)' }}>
            <TextInput id="g-test" label="Send a group test to" type="email" value={testTo} onChange={setTestTo}
              help="Yesterday's group report, or last month's, marked [TEST]." />
            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 22, gap: 'var(--sp-2)' }}>
              <Button loading={testing === 'group'} disabled={!testTo.trim() || testing !== null}
                onClick={() => void test('group')}>Send group daily test</Button>
              <Button variant="secondary" loading={testing === 'group_monthly'} disabled={!testTo.trim() || testing !== null}
                onClick={() => void test('group_monthly')}>Send group monthly test</Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}
